// CORA Agent — GDBS integration
// Only shown for admin / hpc tiers, only after a computation has been registered,
// and only if CORA /api/agent/status responds OK.
// 405 or any request error hides the button silently.

const CORA_BASE       = 'https://cora.getvaultsync.com';
const CORA_STATUS_URL = `${CORA_BASE}/api/agent/status`;
const CORA_AGENT_URL  = `${CORA_BASE}/api/agent/gdbs-agent`;

const ALLOWED_TIERS = ['admin', 'hpc'];

let _coraReady = false;   // status ping passed + tier allowed

// ── Init — ping status and check tier ───────────────────────────────────────
// Does NOT show the button. notifyComputed() does that once a report is ready.
export async function initCoraAgent() {
  // Tier check via auth module
  const license = window._gdbs_report?._license ?? null;
  // getLicense is set on window by auth.js after login
  const tier = (typeof window._getLicense === 'function' ? window._getLicense() : null)?.tier ?? '';
  if (!ALLOWED_TIERS.includes(tier)) return;

  try {
    const res = await fetch(CORA_STATUS_URL, {
      method: 'GET',
      signal: AbortSignal.timeout(4000),
    });
    _coraReady = res.ok;
  } catch { _coraReady = false; }
  // Button stays hidden until notifyComputed() is called
}

// ── Called by registerReport() after each computation ───────────────────────
export function notifyComputed() {
  if (!_coraReady) return;
  const btn = document.getElementById('btn-cora-agent');
  if (btn) btn.classList.remove('hidden');
}

// ── Panel open / close ───────────────────────────────────────────────────────
export function openAgentPanel() {
  document.getElementById('cora-agent-panel').classList.add('open');
  document.getElementById('cora-agent-input').focus();
}

export function closeAgentPanel() {
  document.getElementById('cora-agent-panel').classList.remove('open');
}

// ── Send query ───────────────────────────────────────────────────────────────
export async function sendAgentQuery() {
  const input    = document.getElementById('cora-agent-input');
  const question = input.value.trim();
  if (!question) return;

  const report  = window._gdbs_report ?? {};
  const payload = {
    question,
    activeModule: report.module  ?? '',
    context:      report.params  ?? [],
    data:         report,
  };

  _setState('loading');
  input.value = '';

  try {
    const res = await fetch(CORA_AGENT_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });

    // 405 or server error — endpoint not ready, hide button silently
    if (res.status === 405 || res.status >= 500) {
      _coraReady = false;
      closeAgentPanel();
      document.getElementById('btn-cora-agent')?.classList.add('hidden');
      return;
    }

    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    _renderResponse(json);
  } catch (err) {
    // Network/parse errors — show minimal message, don't crash
    _setState('error', 'CORA unavailable. Try again later.');
  }
}

// ── Rendering ────────────────────────────────────────────────────────────────
function _renderResponse(r) {
  const body = document.getElementById('cora-agent-body');
  let html = `<div class="ca-answer">${_esc(r.answer ?? '(no answer)')}</div>`;

  if (r.hubNames?.length) {
    html += `<div class="ca-hubs"><span class="ca-label">Related:</span>`;
    html += r.hubNames.map(h => `<span class="ca-chip">${_esc(h)}</span>`).join('');
    html += `</div>`;
  }

  if (r.corpusGap) {
    html += `<div class="ca-gap"><i class="fas fa-exclamation-circle"></i> ${_esc(r.corpusGap)}</div>`;
  }

  if (r.found === false) {
    html += `<div class="ca-not-found">No matching knowledge found — answer based on general reasoning.</div>`;
  }

  body.innerHTML = html;
  _setState('done');
}

function _setState(state, msg) {
  const body = document.getElementById('cora-agent-body');
  const btn  = document.getElementById('cora-agent-send');
  if (state === 'loading') {
    body.innerHTML = '<div class="ca-loading"><i class="fas fa-spinner fa-spin"></i> Asking CORA…</div>';
    btn.disabled = true;
  } else if (state === 'error') {
    body.innerHTML = `<div class="ca-error"><i class="fas fa-exclamation-triangle"></i> ${_esc(msg)}</div>`;
    btn.disabled = false;
  } else {
    btn.disabled = false;
  }
}

function _esc(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}
